import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { PaymentInitiationRequest, PaynowService } from '../../api/paynowService';

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  loanId: number;
  loanNumber: string;
  outstandingBalance: number;
  settlementAmount?: number; // Optional settlement/payoff amount
  loanCurrency?: string; // Loan currency (USD, ZIG, ZWG, etc.)
  onPaymentInitiated?: (paymentResponse: any) => void;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({
  open,
  onClose,
  loanId,
  loanNumber,
  outstandingBalance,
  settlementAmount,
  loanCurrency = 'USD', // Default to USD if not provided
  onPaymentInitiated
}) => {
  const [paymentType, setPaymentType] = useState<'PROCESSING_FEE' | 'LOAN_REPAYMENT' | 'INSURANCE_FEE'>('LOAN_REPAYMENT');
  const [amount, setAmount] = useState<string>('');
  // For USD loans, currency is always USD - no selection allowed
  // For other currencies (ZIG, ZWL), allow selection between USD and ZIG
  const [currency, setCurrency] = useState<'USD' | 'ZIG'>('USD');
  const [isSettlementPayment, setIsSettlementPayment] = useState(false);
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [loadingRate, setLoadingRate] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [clientEmail, setClientEmail] = useState<string>('');
  const [clientPhone, setClientPhone] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [polling, setPolling] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState<PaymentInitiationRequest | null>(null);

  // Polling function to check payment status via backend
  const startPolling = (reference: string) => {
    if (!reference) return;
    
    setPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        // Call backend polling endpoint which handles Paynow polling
        const statusResponse = await PaynowService.pollPaymentStatus(reference);
        
        if (statusResponse.paymentStatus === 'PAID' || statusResponse.paymentStatus === 'DELIVERED') {
          setSuccess(`✅ Payment completed! Reference: ${reference}`);
          setPolling(false);
          clearInterval(pollInterval);
          setTimeout(() => {
            onClose();
          }, 3000);
        } else if (statusResponse.paymentStatus === 'FAILED'
            || statusResponse.paymentStatus === 'CANCELLED'
            || statusResponse.paymentStatus === 'DISPUTED'
            || statusResponse.paymentStatus === 'REFUNDED') {
          setError(`Payment ${statusResponse.paymentStatus}: ${statusResponse.failureReason || statusResponse.paymentStatus}`);
          setPolling(false);
          clearInterval(pollInterval);
        } else if (statusResponse.paymentStatus === 'AWAITING_DELIVERY') {
          // Paid on Paynow but delivery not confirmed yet — keep polling
          setSuccess(`Payment received, awaiting delivery confirmation... Reference: ${reference}`);
        } else {
          setSuccess(`Payment in progress... Status: ${statusResponse.paymentStatus || 'PENDING'}. Reference: ${reference}`);
        }
      } catch (err) {
        console.error('Error polling payment status:', err);
        // Continue polling on error
      }
    }, 5000); // Poll every 5 seconds

    // Stop polling after 5 minutes (60 * 5 = 300 seconds)
    setTimeout(() => {
      clearInterval(pollInterval);
      setPolling(false);
      if (success.includes('Payment in progress')) {
        setSuccess(`Payment status check timed out. Please check manually. Reference: ${reference}`);
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    }, 300000);
  };

  const handleSubmit = async () => {
    try {
      setError('');

      // Calculate amount based on payment type
      let amountValue = 0;
      if (paymentType === 'LOAN_REPAYMENT' || paymentType === 'INSURANCE_FEE') {
        amountValue = parseFloat(amount);
        if (!amountValue || amountValue <= 0) {
          setError('Please enter a valid amount');
          return;
        }
        if (paymentType === 'LOAN_REPAYMENT') {
          const maxAmount = settlementAmount && settlementAmount > 0 ? settlementAmount : outstandingBalance;
          if (maxAmount > 0 && amountValue > maxAmount + 0.001) {
            setError(`Amount cannot exceed $${maxAmount.toFixed(2)}`);
            return;
          }
        }
      } else if (paymentType === 'PROCESSING_FEE') {
        // For processing fee, use a default amount or calculate from loan
        amountValue = 50.00; // Default processing fee amount
      }

      // For ZiG payments, ensure we have the active exchange rate
      if (currency === 'ZIG' && paymentType === 'LOAN_REPAYMENT') {
        if (!exchangeRate) {
          setError('Cannot process ZiG payment: Active exchange rate is not available. Please ensure an active rate is configured.');
          return;
        }
      }

      const request: PaymentInitiationRequest = {
        amount: amountValue,
        currency: currency,
        description: description || `${PaynowService.formatPaymentType(paymentType)} for Loan #${loanNumber}`,
        payerEmail: clientEmail || 'user@example.com', // Required field
        payerPhone: clientPhone || undefined,
        paymentType,
        // Loan Details / LoanPaymentSummary pass the Fineract loan account id
        fineractLoanId: loanId,
        additionalInfo: `Loan #${loanNumber} - ${PaynowService.formatPaymentType(paymentType)}`
      };

      // Store payment summary and show confirmation dialog
      setPaymentSummary(request);
      setShowConfirmation(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to prepare payment');
    }
  };

  const handleConfirmPayment = async () => {
    if (!paymentSummary) return;

    try {
      setLoading(true);
      setError('');
      setShowConfirmation(false);

      const response = await PaynowService.initiatePayment(paymentSummary);
      
      if (onPaymentInitiated) {
        onPaymentInitiated(response);
      }

      // Store payment reference for polling
      setPaymentReference(response.paymentReference || response.paynowReference || '');

      // Check payment status and handle accordingly
      if (response.paymentStatus === 'PENDING' && response.redirectUrl) {
        // Payment is pending and has redirect URL - redirect to Paynow gateway
        window.open(response.redirectUrl, '_blank');
        setSuccess(`Payment initiated! Opening payment page... Reference: ${response.paynowReference || response.paymentReference || 'N/A'}`);
        
        // Start polling for payment status updates
        startPolling(response.paymentReference || response.paynowReference || '');
        
        // Don't close dialog immediately - let polling handle it
      } else if (response.paymentStatus === 'PAID' || response.paymentStatus === 'DELIVERED') {
        // Payment completed successfully
        setSuccess(`✅ Payment completed! Reference: ${response.paynowReference || response.paymentReference || 'N/A'}`);
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          onClose();
        }, 2000);
      } else if (response.paymentStatus === 'FAILED' || response.paymentStatus === 'CANCELLED') {
        // Payment failed
        setError(response.failureReason || 'Payment initiation failed');
        return;
      } else {
        // Other status (e.g., AWAITING_DELIVERY, etc.)
        setSuccess(`Payment initiated! Status: ${response.paymentStatus || 'PENDING'}. Reference: ${response.paynowReference || response.paymentReference || 'N/A'}`);
        // Close dialog after a short delay to show success message
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPaymentSummary(null);
  };

  const handleClose = () => {
    if (!loading) {
      setAmount('');
      setCurrency('USD');
      setExchangeRate(null);
      setDescription('');
      setClientEmail('');
      setClientPhone('');
      setError('');
      setSuccess('');
      setPolling(false);
      setPaymentReference('');
      setShowConfirmation(false);
      setPaymentSummary(null);
      onClose();
    }
  };

  const getPaymentAmountInZig = () => {
    if (paymentSummary && paymentSummary.currency === 'ZIG' && exchangeRate) {
      return (paymentSummary.amount * exchangeRate).toFixed(2);
    }
    return null;
  };

  const getPaymentTypeDescription = () => {
    switch (paymentType) {
      case 'PROCESSING_FEE':
        return 'One-time processing fee (automatically calculated)';
      case 'LOAN_REPAYMENT':
        return 'Partial or full loan repayment';
      case 'INSURANCE_FEE':
        return 'Insurance coverage fee';
      default:
        return '';
    }
  };

  // Load active exchange rate when currency is set to ZIG
  React.useEffect(() => {
    if (currency === 'ZIG' && paymentType === 'LOAN_REPAYMENT') {
      setLoadingRate(true);
      PaynowService.getActiveExchangeRate()
        .then((rateData) => {
          setExchangeRate(rateData.rate);
          setError(''); // Clear any previous errors
        })
        .catch((err) => {
          console.error('Failed to load exchange rate:', err);
          setError(err.response?.data?.error || 'Failed to load active exchange rate. ZiG payments require an active rate to be configured.');
          setExchangeRate(null);
        })
        .finally(() => {
          setLoadingRate(false);
        });
    } else {
      setExchangeRate(null);
    }
  }, [currency, paymentType, open]);

  React.useEffect(() => {
    if (!open) {
      setAmount('');
      setCurrency('USD');
      setIsSettlementPayment(false);
      return;
    }

    // Prefill only for settlement (pay off); regular repayment starts blank for user input
    if (settlementAmount && settlementAmount > 0) {
      setIsSettlementPayment(true);
      setAmount(settlementAmount.toString());
      setPaymentType('LOAN_REPAYMENT');
      setDescription(`Pay Off Loan (Settlement) - Loan #${loanNumber}`);
    } else {
      setIsSettlementPayment(false);
      setAmount('');
      setDescription('');
    }
    // outstandingBalance is read at open-time only so typing is not overwritten
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settlementAmount, loanNumber]);

  // Effect to lock currency to USD for USD loans
  React.useEffect(() => {
    if (loanCurrency && loanCurrency.toUpperCase() === 'USD') {
      setCurrency('USD'); // Force USD for USD loans - no selection allowed
    }
  }, [loanCurrency, open]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Initiate Payment</Typography>
          <Chip 
            label={`Loan #${loanNumber}`} 
            color="primary" 
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
            {polling && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="caption">
                  Checking payment status...
                </Typography>
              </Box>
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {isSettlementPayment ? 'Settlement Amount (Pay Off Loan)' : 'Outstanding Balance'}
              </Typography>
              <Typography variant="h6" color={isSettlementPayment ? 'secondary.main' : 'primary'}>
                ${isSettlementPayment && settlementAmount ? settlementAmount.toFixed(2) : outstandingBalance.toFixed(2)}
              </Typography>
              {isSettlementPayment && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  This will pay off the entire loan including principal, interest, fees, and penalties
                </Typography>
              )}
            </Box>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Payment Type</InputLabel>
              <Select
                value={paymentType}
                label="Payment Type"
                onChange={(e) => {
                  const next = e.target.value as typeof paymentType;
                  setPaymentType(next);
                  setIsSettlementPayment(false);
                  if (next === 'LOAN_REPAYMENT') {
                    setAmount(''); // user must enter the amount they want to pay
                  } else if (next === 'INSURANCE_FEE') {
                    setAmount('');
                  } else {
                    setAmount('');
                  }
                }}
                disabled={loading}
              >
                <MenuItem value="PROCESSING_FEE">Processing Fee</MenuItem>
                <MenuItem value="LOAN_REPAYMENT">Loan Repayment{isSettlementPayment ? ' (Pay Off Loan)' : ''}</MenuItem>
                <MenuItem value="INSURANCE_FEE">Insurance Fee</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {isSettlementPayment ? 'Pay off the entire loan including principal, interest, fees, and penalties' : getPaymentTypeDescription()}
            </Typography>
          </Grid>

          {(paymentType === 'LOAN_REPAYMENT' || paymentType === 'INSURANCE_FEE') && (
            <>
              {/* Currency selection - ONLY show for non-USD loans */}
              {/* If loanCurrency is undefined/null, default to showing selection (for backward compatibility) */}
              {loanCurrency && loanCurrency.toUpperCase() !== 'USD' && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Payment Currency</InputLabel>
                    <Select
                      value={currency}
                      label="Payment Currency"
                      onChange={(e) => setCurrency(e.target.value as 'USD' | 'ZIG')}
                      disabled={loading || paymentType !== 'LOAN_REPAYMENT'}
                    >
                      <MenuItem value="USD">USD (US Dollar)</MenuItem>
                      {/* ZIG/ZWG loans can be paid in USD or local currency (ZiG) */}
                      {(loanCurrency.toUpperCase() === 'ZIG' || loanCurrency.toUpperCase() === 'ZWG') && (
                        <MenuItem value="ZIG">ZiG (Zimbabwe Gold)</MenuItem>
                      )}
                    </Select>
                    {paymentType === 'LOAN_REPAYMENT' && (
                      <>
                        {currency === 'ZIG' && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Amount will be converted from USD to ZiG using the active exchange rate
                          </Typography>
                        )}
                        {currency === 'USD' && (loanCurrency.toUpperCase() === 'ZIG' || loanCurrency.toUpperCase() === 'ZWG') && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {loanCurrency} loans can be paid in USD or local currency (ZiG)
                          </Typography>
                        )}
                      </>
                    )}
                  </FormControl>
                </Grid>
              )}
              
              {/* For USD loans, NO currency selection - currency is locked to USD */}
              {loanCurrency && loanCurrency.toUpperCase() === 'USD' && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Payment Currency
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500 }}>
                      USD (US Dollar)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      USD loans can only be repaid in USD. No currency selection available.
                    </Typography>
                  </Box>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={isSettlementPayment ? `Settlement Amount (Pay Off Loan) - ${currency === 'USD' ? 'USD' : 'USD - will be converted to ZiG'}` : `Amount (${currency === 'USD' ? 'USD' : 'USD - will be converted to ZiG'})`}
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={loading || loadingRate}
                  placeholder={paymentType === 'LOAN_REPAYMENT' && !isSettlementPayment ? 'Enter amount to pay' : undefined}
                  inputProps={{
                    min: 0.01,
                    max: paymentType === 'LOAN_REPAYMENT'
                      ? (settlementAmount && settlementAmount > 0 ? settlementAmount : outstandingBalance)
                      : 10000,
                    step: 0.01
                  }}
                  helperText={
                    paymentType === 'LOAN_REPAYMENT'
                      ? currency === 'USD'
                        ? `Enter the amount you want to pay (max $${(
                            settlementAmount && settlementAmount > 0 ? settlementAmount : outstandingBalance
                          ).toFixed(2)})${isSettlementPayment ? ' — settlement amount is suggested for pay-off' : ''}`
                        : loadingRate
                          ? 'Loading exchange rate...'
                          : exchangeRate
                            ? `Enter the amount you want to pay in USD (max $${(
                                settlementAmount && settlementAmount > 0 ? settlementAmount : outstandingBalance
                              ).toFixed(2)} = ${(
                                (settlementAmount && settlementAmount > 0 ? settlementAmount : outstandingBalance) * exchangeRate
                              ).toFixed(2)} ZiG at rate ${exchangeRate.toFixed(4)})`
                            : 'Exchange rate not available'
                      : 'Enter the insurance amount'
                  }
                />
              </Grid>
              
              {paymentType === 'LOAN_REPAYMENT' && currency === 'ZIG' && exchangeRate && amount && (
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Conversion Preview
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {parseFloat(amount).toFixed(2)} USD = {(parseFloat(amount) * exchangeRate).toFixed(2)} ZiG
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Exchange Rate: 1 USD = {exchangeRate.toFixed(4)} ZiG (Active rate)
                    </Typography>
                  </Box>
                </Grid>
              )}
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description (Optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading}
              multiline
              rows={2}
              placeholder={isSettlementPayment ? `Pay Off Loan (Settlement) - Loan #${loanNumber}` : `${PaynowService.formatPaymentType(paymentType)} for Loan #${loanNumber}`}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" gutterBottom>
              Contact Information (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={clientEmail}
              onChange={(e) => setClientEmail(e.target.value)}
              disabled={loading}
              placeholder="your@email.com"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={clientPhone}
              onChange={(e) => setClientPhone(e.target.value)}
              disabled={loading}
              placeholder="+263..."
            />
          </Grid>

          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2">
                Enter the amount you want to pay, then review and confirm your details. You will only be
                redirected to Paynow after you confirm. Supported methods: EcoCash, OneMoney, Telecash,
                VPayments, and Visa/Mastercard.
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading || polling}>
          {polling ? 'Close' : 'Cancel'}
        </Button>
        {polling && paymentReference && (
          <Button 
            onClick={() => startPolling(paymentReference)} 
            variant="outlined"
            disabled={loading}
          >
            Check Status
          </Button>
        )}
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || polling || !amount || (currency === 'ZIG' && !exchangeRate)}
          startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
        >
          {loading ? 'Initiating...' : 'Review Payment'}
        </Button>
      </DialogActions>

      {/* Payment Confirmation Dialog */}
      <Dialog 
        open={showConfirmation} 
        onClose={handleCancelConfirmation}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="primary" />
            <Typography variant="h6">Confirm Payment</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
            <Typography variant="body2">
              Please review the payment details below before confirming. Once confirmed, you will be redirected to Paynow to complete the payment.
            </Typography>
          </Alert>

          {paymentSummary && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PaymentIcon color="primary" />
                  Payment Summary
                </Typography>
                
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Loan Number"
                      secondary={loanNumber}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                    />
                  </ListItem>
                  
                  <Divider component="li" />
                  
                  <ListItem>
                    <ListItemText
                      primary="Payment Type"
                      secondary={PaynowService.formatPaymentType(paymentSummary.paymentType)}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                    />
                  </ListItem>
                  
                  <Divider component="li" />
                  
                  <ListItem>
                    <ListItemText
                      primary="Amount"
                      secondary={
                        <Box>
                          <Typography variant="h6" color="primary" component="span">
                            {paymentSummary.currency === 'USD' 
                              ? `$${paymentSummary.amount.toFixed(2)} USD`
                              : `$${paymentSummary.amount.toFixed(2)} USD = ${getPaymentAmountInZig()} ZiG`
                            }
                          </Typography>
                          {paymentSummary.currency === 'ZIG' && exchangeRate && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Exchange Rate: 1 USD = {exchangeRate.toFixed(4)} ZiG
                            </Typography>
                          )}
                        </Box>
                      }
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                    />
                  </ListItem>
                  
                  <Divider component="li" />
                  
                  <ListItem>
                    <ListItemText
                      primary="Description"
                      secondary={paymentSummary.description || 'No description provided'}
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1' }}
                    />
                  </ListItem>
                  
                  {(paymentSummary.payerEmail || paymentSummary.payerPhone) && (
                    <>
                      <Divider component="li" />
                      <ListItem>
                        <ListItemText
                          primary="Contact Information"
                          secondary={
                            <Box>
                              {paymentSummary.payerEmail && (
                                <Typography variant="body2" component="div">
                                  Email: {paymentSummary.payerEmail}
                                </Typography>
                              )}
                              {paymentSummary.payerPhone && (
                                <Typography variant="body2" component="div">
                                  Phone: {paymentSummary.payerPhone}
                                </Typography>
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        />
                      </ListItem>
                    </>
                  )}
                </List>
              </CardContent>
            </Card>
          )}

          <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Payment Methods Available:</strong> EcoCash, OneMoney, Telecash, VPayments, and Visa/Mastercard
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCancelConfirmation} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmPayment} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {loading ? 'Processing...' : 'Confirm & Go to Paynow'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default PaymentDialog;
